import { useState, useCallback, useEffect, useRef } from "react";
import { useProcessCapture } from "../hooks/useData";
import { logger } from "../logger";
import type {
  PendingModification,
  SpeechRecognition,
  ModificationResult,
  SpeechRecognitionEvent,
  SpeechRecognitionErrorEvent,
  UpdateRecord,
} from "../model";

interface VoiceCaptureSidebarProps {
  onModificationsApproved?: (modifications: ModificationResult) => void;
  projectId?: number;
  isOpen: boolean;
  onClose: () => void;
}

export default function VoiceCaptureSidebar({
  onModificationsApproved,
  projectId,
  isOpen,
  onClose,
}: VoiceCaptureSidebarProps) {
  const [recording, setRecording] = useState(false);
  const [capture, setCapture] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [isSupported, setIsSupported] = useState(false);
  const [pendingModification, setPendingModification] =
    useState<PendingModification | null>(null);
  const [pendingUpdates, setPendingUpdates] = useState<UpdateRecord[]>([]);
  const [rejectedUpdates, setRejectedUpdates] = useState<UpdateRecord[]>([]);
  const [acceptedUpdates, setAcceptedUpdates] = useState<UpdateRecord[]>([]);
  const [autoProcessingScheduled, setAutoProcessingScheduled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const lastProcessedCapture = useRef("");
  const processingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const processCapture = useProcessCapture(projectId);

  const processCurrentCapture = useCallback(async () => {
    if (capture.length < 10 || capture === lastProcessedCapture.current) {
      return;
    }

    try {
      const result = await processCapture.mutateAsync({
        transcript: capture,
        pendingUpdates,
        rejectedUpdates,
        acceptedUpdates,
      });

      const modificationProposal: PendingModification = {
        originalTranscript: capture,
        proposedModifications: result.modifications,
        timestamp: new Date().toLocaleString(),
      };

      setPendingModification(modificationProposal);
      lastProcessedCapture.current = capture;
    } catch (error) {
      logger.error("Error processing capture", { error });
    }
  }, [
    capture,
    pendingUpdates,
    rejectedUpdates,
    acceptedUpdates,
    processCapture,
  ]);

  const scheduleAutoProcessing = useCallback(
    (text: string) => {
      // Clear any existing timeouts
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        setAutoProcessingScheduled(false);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        setCountdown(0);
      }

      // Only auto-process if we have enough content and it's different from last processed
      if (
        text.length >= 10 &&
        text !== lastProcessedCapture.current &&
        !processCapture.isPending
      ) {
        setAutoProcessingScheduled(true);
        setCountdown(3);

        // Start countdown
        countdownIntervalRef.current = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current!);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        processingTimeoutRef.current = setTimeout(() => {
          setAutoProcessingScheduled(false);
          setCountdown(0);
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
          processCurrentCapture();
        }, 3000); // 3 seconds debounce
      }
    },
    [processCapture.isPending, processCurrentCapture],
  );

  const handleCaptureChange = (newValue: string) => {
    setCapture(newValue);
    scheduleAutoProcessing(newValue);
  };

  const clearCapture = () => {
    setCapture("");
    setInterimTranscript("");
    lastProcessedCapture.current = "";
    setPendingUpdates([]);
    setRejectedUpdates([]);
    setAcceptedUpdates([]);
    setPendingModification(null);
    // Clear any pending auto-processing
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setAutoProcessingScheduled(false);
    setCountdown(0);
  };

  const approveModifications = async () => {
    if (pendingModification) {
      if (onModificationsApproved) {
        onModificationsApproved(pendingModification.proposedModifications);
      }

      const updateRecord: UpdateRecord = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        type: "accepted",
        modification: pendingModification,
      };
      setAcceptedUpdates([...acceptedUpdates, updateRecord]);
      setPendingModification(null);

      // Clear capture after successful application
      clearCapture();
    }
  };

  const rejectModifications = () => {
    if (pendingModification) {
      const updateRecord: UpdateRecord = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        type: "rejected",
        modification: pendingModification,
      };
      setRejectedUpdates([...rejectedUpdates, updateRecord]);
      setPendingModification(null);
    }
  };

  const initializeSpeechRecognition = useCallback(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      logger.error("Speech recognition not supported in this browser");
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onstart = () => {
      logger.debug("Speech recognition started");
      setRecording(true);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = "";
      let interimText = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimText += transcript;
        }
      }

      if (finalTranscript) {
        setCapture((prev) => {
          const newCapture = prev
            ? prev + " " + finalTranscript
            : finalTranscript;
          // Schedule auto-processing for speech input
          scheduleAutoProcessing(newCapture);
          return newCapture;
        });
      }
      setInterimTranscript(interimText);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      logger.error("Speech recognition error", { error: event.error });
    };

    recognition.onend = () => {
      logger.debug("Speech recognition ended");
      setRecording(false);
      setInterimTranscript("");
    };

    recognitionRef.current = recognition;
  }, [scheduleAutoProcessing]);

  const toggleRecording = () => {
    if (!isSupported) {
      alert(
        "Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.",
      );
      return;
    }

    if (recording) {
      // Force stop recording with better error handling
      try {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
          logger.debug("Speech recognition stopped manually");
        } else {
          logger.warn("No recognition instance to stop");
          // Force set recording to false if no instance exists
          setRecording(false);
          setInterimTranscript("");
        }
      } catch (err) {
        logger.error("Failed to stop recognition", { error: err });
        // Force set recording to false even if stop fails
        setRecording(false);
        setInterimTranscript("");
      }
      return;
    }

    // Starting recording
    try {
      if (!recognitionRef.current) {
        logger.debug("No recognition instance, initializing...");
        initializeSpeechRecognition();
      }

      if (recognitionRef.current) {
        recognitionRef.current.start();
        logger.debug("Speech recognition started");
      } else {
        throw new Error("Failed to initialize speech recognition");
      }
    } catch (err) {
      logger.error("Failed to start recognition", { error: err });
      alert("Failed to start recording. Please try again.");
    }
  };

  useEffect(() => {
    if (isOpen) {
      initializeSpeechRecognition();
    } else {
      // When closing the panel, stop any active recording
      if (recording && recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          logger.debug("Speech recognition stopped due to panel close");
        } catch (err) {
          logger.error("Failed to stop recognition on panel close", {
            error: err,
          });
        }
        setRecording(false);
        setInterimTranscript("");
      }
    }

    // Cleanup timeouts on unmount
    return () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      // Stop recording on unmount
      if (recording && recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          logger.debug("Speech recognition stopped on unmount");
        } catch (err) {
          logger.error("Failed to stop recognition on unmount", { error: err });
        }
      }
    };
  }, [isOpen, initializeSpeechRecognition, recording]);

  // Handle Escape key to close sidebar
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Right Side Panel - No Overlay */}
      <div
        className={`fixed right-0 top-0 h-full w-96 bg-white border-l border-gray-200 shadow-2xl z-20 flex flex-col transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div
                className={`w-4 h-4 rounded-full ${recording ? "bg-red-500 animate-pulse" : "bg-blue-500"}`}
              ></div>
              {recording && (
                <div className="absolute inset-0 w-4 h-4 bg-red-500 rounded-full animate-ping"></div>
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                Voice Capture
                {recording && (
                  <span className="text-sm text-red-600 font-normal animate-pulse">
                    Recording...
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                {projectId && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    📋 Project Mode
                  </span>
                )}
                {isSupported ? (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    🎤 Ready
                  </span>
                ) : (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full">
                    ⚠️ Unsupported
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200 group"
            aria-label="Close capture sidebar"
          >
            <svg
              className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content - Vertical Layout */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {!isSupported && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
              Speech recognition is not supported in this browser. Please use
              Chrome, Edge, or Safari.
            </div>
          )}

          {/* Recording Controls */}
          <div className="flex gap-2">
            <button
              onClick={toggleRecording}
              disabled={!isSupported}
              className={`flex-1 px-4 py-2 rounded font-medium ${
                recording
                  ? "bg-red-500 text-white hover:bg-red-600"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              } disabled:bg-gray-400 disabled:cursor-not-allowed`}
            >
              {recording ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                  Stop Recording
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3zM19 10v1a7 7 0 0 1-14 0v-1h2v1a5 5 0 0 0 10 0v-1h2z" />
                  </svg>
                  Start Recording
                </span>
              )}
            </button>
          </div>

          {/* Interim Speech Display */}
          {interimTranscript && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
              <div className="text-xs text-yellow-700 font-medium mb-1">
                Speaking...
              </div>
              <div className="text-sm text-yellow-800">{interimTranscript}</div>
            </div>
          )}

          {/* Capture Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Captured Content
              {capture && (
                <span className="ml-2 text-xs text-gray-500">
                  ({capture.split(/\s+/).filter((w) => w).length} words)
                </span>
              )}
            </label>
            <textarea
              value={capture}
              onChange={(e) => handleCaptureChange(e.target.value)}
              placeholder={
                projectId
                  ? "Start speaking or type to capture ideas for this project..."
                  : "Start speaking or type to capture ideas..."
              }
              className="w-full h-32 p-3 border border-gray-300 rounded resize-none text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {capture && (
              <div className="flex justify-between items-center mt-2">
                <div className="flex gap-2">
                  <button
                    onClick={processCurrentCapture}
                    disabled={processCapture.isPending || capture.length < 10}
                    className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {processCapture.isPending
                      ? "Processing..."
                      : "Process Capture"}
                  </button>
                  <button
                    onClick={clearCapture}
                    className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
                  >
                    Clear
                  </button>
                </div>

                {/* Auto-processing indicator */}
                {autoProcessingScheduled && countdown > 0 && (
                  <div className="flex items-center gap-2 text-xs text-blue-600">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                    <span>Auto-processing in {countdown}s...</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Processing Status */}
          {processCapture.isPending && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm text-blue-700">
                  Processing with AI...
                </span>
              </div>
            </div>
          )}

          {/* Pending Modifications */}
          {pendingModification ? (
            <div className="border-2 border-green-500 rounded-lg">
              {/* Header */}
              <div className="bg-green-50 p-3 border-b border-green-200">
                <h3 className="text-green-700 font-semibold text-sm">
                  🔄 AI Modifications Ready
                </h3>
                <div className="text-xs text-green-600 mt-1">
                  Based on: "
                  {pendingModification.originalTranscript.substring(0, 50)}..."
                </div>
              </div>

              {/* Modifications List */}
              <div className="p-3 max-h-64 overflow-y-auto">
                {pendingModification.proposedModifications.projectModifications
                  .length > 0 && (
                  <div className="mb-3">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">
                      Project Changes:
                    </h4>
                    {pendingModification.proposedModifications.projectModifications.map(
                      (mod, index) => (
                        <div
                          key={index}
                          className="mb-2 p-2 bg-gray-50 rounded text-xs"
                        >
                          <span
                            className={`font-medium ${
                              mod.operation === "create"
                                ? "text-green-600"
                                : mod.operation === "update"
                                  ? "text-yellow-600"
                                  : "text-red-600"
                            }`}
                          >
                            {mod.operation.toUpperCase()}
                          </span>
                          {mod.name && (
                            <span className="ml-1">Project: {mod.name}</span>
                          )}
                          {mod.desiredOutcome && (
                            <div className="text-gray-600 mt-1">
                              Outcome: {mod.desiredOutcome}
                            </div>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                )}

                {pendingModification.proposedModifications
                  .actionItemModifications.length > 0 && (
                  <div className="mb-3">
                    <h4 className="text-xs font-semibold text-gray-700 mb-2">
                      Action Item Changes:
                    </h4>
                    {pendingModification.proposedModifications.actionItemModifications.map(
                      (mod, index) => (
                        <div
                          key={index}
                          className="mb-2 p-2 bg-gray-50 rounded text-xs"
                        >
                          <span
                            className={`font-medium ${
                              mod.operation === "create"
                                ? "text-green-600"
                                : mod.operation === "update"
                                  ? "text-yellow-600"
                                  : "text-red-600"
                            }`}
                          >
                            {mod.operation.toUpperCase()}
                          </span>
                          {mod.content && (
                            <span className="ml-1">"{mod.content}"</span>
                          )}
                          {mod.isGlobal ? (
                            <span className="text-gray-500 ml-1">(Global)</span>
                          ) : (
                            mod.projectId && (
                              <span className="text-gray-500 ml-1">
                                (Project #{mod.projectId})
                              </span>
                            )
                          )}
                        </div>
                      ),
                    )}
                  </div>
                )}

                {pendingModification.proposedModifications.projectModifications
                  .length === 0 &&
                  pendingModification.proposedModifications
                    .actionItemModifications.length === 0 && (
                    <div className="text-gray-500 text-xs italic">
                      No modifications proposed.
                    </div>
                  )}
              </div>

              {/* Action Buttons */}
              <div className="p-3 border-t border-green-200 flex gap-2">
                <button
                  onClick={approveModifications}
                  className="flex-1 px-3 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 font-medium"
                >
                  ✓ Apply Changes
                </button>
                <button
                  onClick={rejectModifications}
                  className="px-3 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                >
                  ✗ Reject
                </button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 text-xs text-gray-500">
          Press <kbd className="px-1 py-0.5 bg-gray-100 rounded">Esc</kbd> to
          close
        </div>
      </div>
    </>
  );
}
